import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  cloneElement,
  isValidElement,
} from "react";

const VideoUploadContext = createContext(null);

export const VideoUploadProvider = ({ children, ...rest }) => {
  const [pendingFiles, setPendingFiles] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [uploadProgress, setUploadProgressState] = useState({});

  const setPendingFile = useCallback((source, file) => {
    setPendingFiles(prev => ({ ...prev, [source]: file }));
    setUploadErrors(prev => {
      if (!prev[source]) return prev;
      const next = { ...prev };
      delete next[source];
      return next;
    });
  }, []);

  const clearPendingFile = useCallback(source => {
    setPendingFiles(prev => {
      const next = { ...prev };
      delete next[source];
      return next;
    });
  }, []);

  const clearAllPendingFiles = useCallback(() => {
    setPendingFiles({});
  }, []);

  const setUploadError = useCallback((source, message) => {
    setUploadErrors(prev => ({ ...prev, [source]: message }));
  }, []);

  const clearUploadError = useCallback(source => {
    setUploadErrors(prev => {
      if (!prev[source]) return prev;
      const next = { ...prev };
      delete next[source];
      return next;
    });
  }, []);

  const clearAllUploadErrors = useCallback(() => {
    setUploadErrors({});
  }, []);

  const setUploadProgress = useCallback((source, percent) => {
    setUploadProgressState(prev => ({ ...prev, [source]: percent }));
  }, []);

  const clearUploadProgress = useCallback(source => {
    setUploadProgressState(prev => {
      if (prev[source] == null) return prev;
      const next = { ...prev };
      delete next[source];
      return next;
    });
  }, []);

  const clearAllUploadProgress = useCallback(() => {
    setUploadProgressState({});
  }, []);

  const getPendingFile = useCallback(
    source => pendingFiles[source] || null,
    [pendingFiles]
  );

  const getUploadError = useCallback(
    source => uploadErrors[source] || null,
    [uploadErrors]
  );

  const getUploadProgress = useCallback(
    source =>
      uploadProgress[source] != null ? uploadProgress[source] : null,
    [uploadProgress]
  );

  const hasPendingFile = useCallback(
    source => Boolean(pendingFiles[source]),
    [pendingFiles]
  );

  const value = useMemo(
    () => ({
      pendingFiles,
      uploadErrors,
      uploadProgress,
      setPendingFile,
      clearPendingFile,
      clearAllPendingFiles,
      setUploadError,
      clearUploadError,
      clearAllUploadErrors,
      setUploadProgress,
      clearUploadProgress,
      clearAllUploadProgress,
      getPendingFile,
      getUploadError,
      getUploadProgress,
      hasPendingFile,
    }),
    [
      pendingFiles,
      uploadErrors,
      uploadProgress,
      setPendingFile,
      clearPendingFile,
      clearAllPendingFiles,
      setUploadError,
      clearUploadError,
      clearAllUploadErrors,
      setUploadProgress,
      clearUploadProgress,
      clearAllUploadProgress,
      getPendingFile,
      getUploadError,
      getUploadProgress,
      hasPendingFile,
    ]
  );

  return (
    <VideoUploadContext.Provider value={value}>
      {isValidElement(children)
        ? cloneElement(children, rest)
        : children}
    </VideoUploadContext.Provider>
  );
};

export const useVideoUpload = () => {
  const context = useContext(VideoUploadContext);
  if (!context) {
    throw new Error("useVideoUpload must be used within VideoUploadProvider");
  }
  return context;
};
